import { Component, type ErrorInfo, type ReactNode } from 'react';

import NaverMap, { NaverMapMarkerItem } from './BaseMap';
import { NAVER_MAP_CLIENT_ID } from './config/keys';

const MAP_LOAD_ERROR_MESSAGE = '지도를 로드할 수 없습니다.';

interface ShopNaverMapProps {
  clientId?: string;
  id?: string | number;
  name: string;
  address?: string;
  lat: number;
  lon?: number;
  lng?: number;
  zoomLevel?: number;
  minHeight?: number;
  className?: string;
  style?: React.CSSProperties;
}

interface MapErrorBoundaryProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  minHeight?: number;
}

interface MapErrorBoundaryState {
  hasError: boolean;
}

class MapErrorBoundary extends Component<
  MapErrorBoundaryProps,
  MapErrorBoundaryState
> {
  state: MapErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className={this.props.className}
          style={{
            width: '100%',
            height: '100%',
            minHeight: this.props.minHeight || 220,
            borderRadius: 16,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#F7F2EA',
            color: '#536079',
            fontSize: 13,
            textAlign: 'center',
            padding: 16,
            ...this.props.style,
          }}
        >
          {MAP_LOAD_ERROR_MESSAGE}
        </div>
      );
    }

    return this.props.children;
  }
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export default function ShopNaverMap({
  clientId = NAVER_MAP_CLIENT_ID,
  id = 'shop-location',
  name,
  address,
  lat,
  lon,
  lng,
  zoomLevel = 16,
  minHeight = 220,
  className,
  style,
}: ShopNaverMapProps) {
  const markerLng = Number(lng ?? lon);
  const markerLat = Number(lat);
  const hasValidCoords = Number.isFinite(markerLat) && Number.isFinite(markerLng);
  const safeName = escapeHtml(name || '업체 위치');
  const safeAddress = address ? escapeHtml(address) : '';

  const markers: NaverMapMarkerItem[] = hasValidCoords
    ? [
        {
          id,
          name,
          lat: markerLat,
          lng: markerLng,
          icon: {
            content: `
              <div style="
                transform: translateY(-8px);
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 6px;
                pointer-events: auto;
              ">
                
                <div style="
                  width: 18px;
                  margin-left: -9px;
                  height: 18px;
                  border-radius: 9999px;
                  background: #B4563F;
                  border: 3px solid #ffffff;
                  box-shadow: 0 2px 8px rgba(18,27,53,0.24);
                "></div>
              </div>>
            `,
            size: undefined,
            anchor: undefined,
          },
          html: `
            <div style="padding:10px 12px; min-width:150px;">
              <div style="font-size:13px; font-weight:700; margin-bottom:4px; text-align: center">
                ${safeName}
              </div>
              </div>
              `,
              // ${
              //   safeAddress
              //     ? `<div style="font-size:12px; color:#536079; line-height:1.45;">${safeAddress}</div>`
              //     : ''
              // }
        },
      ]
    : [];

  return (
    <MapErrorBoundary className={className} style={style} minHeight={minHeight}>
      <NaverMap
        clientId={clientId}
        center={hasValidCoords ? { lat: markerLat, lng: markerLng } : undefined}
        markers={markers}
        selectedMarkerId={hasValidCoords ? id : null}
        autoFit={false}
        zoom={zoomLevel}
        connectLine={false}
        minHeight={minHeight}
        style={{ width: '100%', height: '100%' }}
      />
    </MapErrorBoundary>
  );
}
{/* <div style="
                transform: translateY(-6px);
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 6px;
                pointer-events: auto;
              ">
                <div style="
                  max-width: 180px;
                  padding: 7px 10px;
                  border-radius: 9999px;
                  background: #121B35;
                  color: #ffffff;
                  border: 1px solid rgba(255,255,255,0.9);
                  box-shadow: 0 6px 16px rgba(18,27,53,0.22);
                  font-size: 12px;
                  font-weight: 700;
                  line-height: 1.2;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                ">
                  ${safeName}
                </div>
                <div style="
                  width: 18px;
                  height: 18px;
                  border-radius: 9999px;
                  background: #B9965B;
                  border: 3px solid #ffffff;
                  box-shadow: 0 2px 8px rgba(18,27,53,0.24);
                "></div>
              </div> */}
